/**
 * `composeBranding` — Story 9.7 CSD #2 lifted helper.
 *
 * Originally lived at `web/src/submitter/composeBranding.ts` (Story 8.6
 * CSD #21 extraction from `<PreLinkPage>`). Story 9.7 lifts the helper
 * into the shared `web/src/branding/` module so the new voter pages
 * (`<VoterLandingPage>` + `<VoterGalleryPage>`) can reuse it without
 * the voter chunk pulling in submitter code (CSD #9 chunk-membership
 * lock).
 *
 * Bridges the API's flat `Branding` shape (`SubmitLanding.branding`
 * AND `VoteLanding.branding` — both reference the same `components[
 * 'schemas']['Branding']` schema) to Story 5.6's `<BrandingProvider>`
 * `Branding` interface. The OpenAPI schema lacks `eventId` (path
 * parameter, not body) and types `logoUrl: string`; Story 5.6's
 * `Branding` interface requires `eventId: string` and types
 * `logoUrl: string | null`. This helper spreads the flat OpenAPI
 * fields, adds `eventId` from URL params, and coerces empty/falsy
 * `logoUrl` to `null` so consumers can render a logo OR a fallback
 * heading without truthiness gymnastics.
 *
 * Consumers (Story 9.7 lift expanded the consumer set):
 *   - Story 8.4 — `<PreLinkPage>` Step A landing fetch.
 *   - Story 8.6 — `<UploadPage>` Step C/D/E landing fetch.
 *   - Story 9.7 — `<VoterLandingPage>` landing fetch.
 *   - Story 9.7 — `<VoterGalleryPage>` re-renders branded gallery.
 *
 * References:
 *   - [Source: _bmad-output/implementation-artifacts/9-7-...md CSD #2]
 *   - [Source: web/src/branding/BrandingContext.ts — Branding interface]
 */
import type { Branding } from './BrandingContext';
import type { components } from '../api/types';

type SubmitLanding = components['schemas']['SubmitLanding'];
type VoteLanding = components['schemas']['VoteLanding'];
type BroadcastJoin = components['schemas']['BroadcastJoin'];
type BroadcastBoard = components['schemas']['BroadcastBoard'];

/**
 * Type alias for the flat branding shape carried by `SubmitLanding`,
 * `VoteLanding`, `BroadcastJoin`, AND `BroadcastBoard` payloads — they
 * all share the same `components['schemas']['Branding']` type at the
 * source.
 *
 * Story 12.3 CSD #5 — `BroadcastJoin['branding']` added so
 * `<BroadcastJoinPage>` can reuse this helper.
 *
 * Story 12.4 CSD #11 — `BroadcastBoard['branding']` added (third
 * widening) so `<BroadcastBoardPage>` can reuse this helper. Structurally
 * identical to the existing union members.
 */
export type LandingBranding =
  | SubmitLanding['branding']
  | VoteLanding['branding']
  | BroadcastJoin['branding']
  | BroadcastBoard['branding'];

/**
 * Compose a Story 5.6-shaped `Branding` from a Story 8.1 / 9.1 landing
 * payload's flat branding object plus the URL-derived eventId. Pure
 * function; consumers wrap in `useMemo` per Story 8.4 CSD #10 sub-lock
 * to keep the BrandingProvider value stable across renders.
 *
 * @param branding A landing payload's `branding` field (8 flat fields).
 * @param eventId  ULID extracted from the route's `:eventId` param.
 * @returns        A `Branding` object suitable for `<BrandingProvider branding={...}>`.
 */
export function composeBranding(
  branding: LandingBranding,
  eventId: string,
): Branding {
  return {
    eventId,
    brandPrimaryDark: branding.brandPrimaryDark,
    brandPrimaryLight: branding.brandPrimaryLight,
    brandOnPrimaryDark: branding.brandOnPrimaryDark,
    brandOnPrimaryLight: branding.brandOnPrimaryLight,
    brandSoft: branding.brandSoft,
    brandStrong: branding.brandStrong,
    logoUrl: branding.logoUrl || null,
  };
}

// Re-export the Branding type so consumers can import a single module.
export type { Branding } from './BrandingContext';
